/**
 * Tesla-style product configurator.
 *
 * Standalone custom element — intentionally does NOT import from @theme/*
 * so it can't conflict with the theme's own variant picker. Reads product
 * JSON embedded in the section, listens to radio changes, computes the
 * matching variant, and swaps price / media / spec bar / URL live.
 */
(function () {
  if (customElements.get('tesla-configurator')) return;

  /** Format a Shopify money value (in cents) using shop.money_format. */
  function formatMoney(cents, format) {
    if (cents == null || isNaN(cents)) return '';
    const amount = (cents / 100).toFixed(2);
    const [intPart, decPart] = amount.split('.');
    const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const formatted = `${withThousands},${decPart}`;
    if (format && format.includes('{{')) {
      return format.replace(/\{\{\s*amount[^}]*\}\}/, formatted);
    }
    return `${formatted} kr`;
  }

  class TeslaConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      const jsonScript = this.querySelector('script[data-product-json]');
      try {
        this.product = JSON.parse(jsonScript.textContent);
      } catch (e) {
        console.error('[tesla-configurator] Failed to parse product JSON', e);
        return;
      }

      this.moneyFormat = this.dataset.moneyFormat || '';
      this.form = this.querySelector('[data-ref="form"]');
      this.variantIdInput = this.querySelector('[data-ref="variantIdInput"]');
      this.propVariant = this.querySelector('[data-ref="propVariant"]');
      this.propPrice = this.querySelector('[data-ref="propPrice"]');
      this.feeVariantId = this.querySelector('[data-ref="feeVariantId"]');
      this.taxEl = this.querySelector('[data-ref="tax"]');
      this.priceEl = this.querySelector('[data-ref="price"]');
      this.compareRow = this.querySelector('[data-ref="compareRow"]');
      this.compareEl = this.querySelector('[data-ref="compare"]');
      this.ctaEl = this.querySelector('[data-ref="cta"]');
      this.ctaLabelEl = this.querySelector('[data-ref="ctaLabel"]');
      this.heroImageEl = this.querySelector('[data-ref="heroImage"]');
      this.mediaPane = this.querySelector('[data-ref="mediaPane"]');

      this.specRefs = {
        range_km: this.querySelector('[data-ref="specRange"]'),
        top_speed: this.querySelector('[data-ref="specTopSpeed"]'),
        charge_time: this.querySelector('[data-ref="specChargeTime"]'),
      };

      this.radios = Array.from(this.querySelectorAll('input[type="radio"][data-option-index]'));
      this.groups = Array.from(this.querySelectorAll('.tesla-configurator__group'));

      // Precompute card price deltas against the current base variant
      this.updateCardPrices(this.getCurrentVariant());

      this.addEventListener('change', this.onChange.bind(this));

      // Sync from URL ?variant= if present
      const urlVariantId = this.getUrlVariantId();
      if (urlVariantId && String(urlVariantId) !== String(this.dataset.currentVariantId)) {
        const v = this.product.variants.find((x) => String(x.id) === String(urlVariantId));
        if (v) this.selectVariant(v, { pushState: false });
      }
    }

    getUrlVariantId() {
      try {
        return new URL(window.location.href).searchParams.get('variant');
      } catch (e) {
        return null;
      }
    }

    onChange(event) {
      const target = event.target;
      if (!target || target.type !== 'radio' || target.dataset.optionIndex == null) return;
      const variant = this.findMatchingVariant();
      if (!variant) return;
      this.selectVariant(variant);
    }

    /** Return the current Shopify variant based on checked radios. */
    findMatchingVariant() {
      const selectedValues = this.groups.map((group) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
      return this.product.variants.find((v) => {
        const vOpts = [v.option1, v.option2, v.option3];
        return selectedValues.every((val, i) => val == null || val === vOpts[i]);
      });
    }

    getCurrentVariant() {
      const id = this.variantIdInput ? this.variantIdInput.value : this.dataset.currentVariantId;
      return this.product.variants.find((v) => String(v.id) === String(id)) || this.product.variants[0];
    }

    selectVariant(variant, { pushState = true } = {}) {
      // Update hidden input (only if no fee product override)
      if (this.variantIdInput) this.variantIdInput.value = variant.id;
      this.dataset.currentVariantId = variant.id;

      // Update line item properties for order fee flow
      if (this.propVariant) this.propVariant.value = variant.title;
      if (this.propPrice) this.propPrice.value = formatMoney(variant.price, this.moneyFormat);

      // Match fee variant by color name
      if (this.feeVariantId && window.__feeVariantMap) {
        const colorName = variant.option1 || variant.title;
        const feeId = window.__feeVariantMap[colorName];
        if (feeId) this.feeVariantId.value = feeId;
      }

      // Sync radios to variant option values (handles URL-based selection)
      const vOpts = [variant.option1, variant.option2, variant.option3];
      this.groups.forEach((group, i) => {
        const wanted = vOpts[i];
        if (wanted == null) return;
        const radio = group.querySelector(`input[type="radio"][value="${CSS.escape(wanted)}"]`);
        if (radio && !radio.checked) radio.checked = true;
        const selectedLabel = group.querySelector(`[data-ref="selectedValue-${i}"]`);
        if (selectedLabel) selectedLabel.textContent = wanted;
      });

      // Price + tax
      if (this.priceEl) this.priceEl.textContent = formatMoney(variant.price, this.moneyFormat);
      if (this.taxEl) this.taxEl.textContent = 'Inkluderer moms på ' + formatMoney(Math.round(variant.price * 0.2), this.moneyFormat);
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        if (this.compareEl) this.compareEl.textContent = formatMoney(variant.compare_at_price, this.moneyFormat);
        if (this.compareRow) this.compareRow.hidden = false;
      } else {
        if (this.compareRow) this.compareRow.hidden = true;
      }

      // CTA availability
      if (this.ctaEl) {
        this.ctaEl.disabled = !variant.available;
        if (this.ctaLabelEl) {
          this.ctaLabelEl.textContent = variant.available
            ? this.ctaEl.dataset.labelAvailable || this.ctaLabelEl.textContent || 'Bestil nu'
            : 'Udsolgt';
        }
      }

      // Media swap
      this.swapMedia(variant);

      // Spec bar — from variant metafields (injected via JSON)
      this.updateSpecBar(variant);

      // Update card price deltas relative to new selection
      this.updateCardPrices(variant);

      // Update URL
      if (pushState) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) { /* noop */ }
      }
    }

    swapMedia(variant) {
      if (!this.heroImageEl || !variant.featured_image || !variant.featured_image.src) return;
      const src = variant.featured_image.src;
      if (this.heroImageEl.src && this.heroImageEl.src.indexOf(src.split('?')[0]) !== -1) return;

      this.heroImageEl.dataset.loading = 'true';
      const img = new Image();
      img.onload = () => {
        this.heroImageEl.src = src;
        this.heroImageEl.srcset = '';
        this.heroImageEl.dataset.loading = 'false';
      };
      img.onerror = () => {
        this.heroImageEl.dataset.loading = 'false';
      };
      img.src = src;
    }

    updateSpecBar(variant) {
      // Shopify product.js doesn't include metafields. We stash them on the
      // variant via a side-channel: the variant's `public_title` isn't reliable,
      // so instead we look them up from window-scoped data if present, or from
      // data-* attributes rendered server-side per variant. Here we read from
      // a global map keyed by variant id, populated by an optional inline script.
      const map = window.__teslaVariantSpecs && window.__teslaVariantSpecs[variant.id];
      if (!map) return;
      ['range_km', 'top_speed', 'charge_time'].forEach((key) => {
        const el = this.specRefs[key];
        if (!el) return;
        const wrapper = el.closest('[data-spec]');
        if (map[key]) {
          el.textContent = map[key];
          if (wrapper) wrapper.hidden = false;
        } else if (wrapper) {
          wrapper.hidden = true;
        }
      });
    }

    /**
     * Show price differences on option cards relative to the current variant.
     * For each option value, we find the cheapest available variant that
     * matches the currently-selected values in OTHER groups plus this value,
     * and show the diff against the current price.
     */
    updateCardPrices(currentVariant) {
      if (!currentVariant) return;
      const basePrice = currentVariant.price;
      const selectedValues = this.groups.map((group) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });

      this.groups.forEach((group, gi) => {
        const cards = group.querySelectorAll('[data-ref^="cardPrice-"]');
        cards.forEach((cardPriceEl) => {
          const optionValue = cardPriceEl.dataset.optionValue;
          const hypothetical = selectedValues.slice();
          hypothetical[gi] = optionValue;

          const match = this.product.variants.find((v) => {
            const vOpts = [v.option1, v.option2, v.option3];
            return hypothetical.every((val, i) => val == null || val === vOpts[i]);
          });

          if (!match) {
            cardPriceEl.textContent = '';
            return;
          }

          const diff = match.price - basePrice;
          if (diff === 0) {
            // Same as current — show nothing or "Inkluderet"
            cardPriceEl.textContent = optionValue === selectedValues[gi] ? '' : 'Inkluderet';
          } else if (diff > 0) {
            cardPriceEl.textContent = `+${formatMoney(diff, this.moneyFormat)}`;
          } else {
            cardPriceEl.textContent = `−${formatMoney(Math.abs(diff), this.moneyFormat)}`;
          }
        });
      });
    }
  }

  customElements.define('tesla-configurator', TeslaConfigurator);
})();
